/**
 * Shared constants for Meta/Facebook optimization goals and their human-readable labels
 * This file provides a single source of truth for optimization goal translations
 * across the Social application.
 */

// Human-readable labels for optimization goals
export const OPTIMIZATION_GOAL_LABELS: Record<string, string> = {
  REACH: 'Reach',
  IMPRESSIONS: 'Impressions',
  AD_RECALL_LIFT: 'Ad Recall Lift',
  LINK_CLICKS: 'Link Clicks',
  LANDING_PAGE_VIEWS: 'Landing Page Views',
  POST_ENGAGEMENT: 'Post Engagement',
  EVENT_RESPONSES: 'Event Responses',
  VIDEO_VIEWS: 'Video Views',
  LEAD_GENERATION: 'Lead Generation',
  OFFSITE_CONVERSIONS: 'Offsite Conversions',
  APP_INSTALLS: 'App Installs',
};

// Valid optimization goals for each campaign objective
export const VALID_OPTIMIZATION_GOALS: Record<string, string[]> = {
  OUTCOME_AWARENESS: ['REACH', 'IMPRESSIONS', 'AD_RECALL_LIFT'],
  OUTCOME_TRAFFIC: ['LINK_CLICKS', 'LANDING_PAGE_VIEWS'],
  OUTCOME_ENGAGEMENT: ['POST_ENGAGEMENT', 'EVENT_RESPONSES', 'VIDEO_VIEWS'],
  OUTCOME_LEADS: ['LEAD_GENERATION', 'OFFSITE_CONVERSIONS'],
  OUTCOME_SALES: ['OFFSITE_CONVERSIONS', 'LINK_CLICKS'],
  OUTCOME_APP_PROMOTION: ['APP_INSTALLS'],
};

// Valid billing events for each optimization goal
export const VALID_BILLING_EVENTS: Record<string, string[]> = {
  REACH: ['IMPRESSIONS'],
  IMPRESSIONS: ['IMPRESSIONS'],
  AD_RECALL_LIFT: ['IMPRESSIONS'],
  LANDING_PAGE_VIEWS: ['IMPRESSIONS'],
  POST_ENGAGEMENT: ['IMPRESSIONS'],
  EVENT_RESPONSES: ['IMPRESSIONS'],
  VIDEO_VIEWS: ['IMPRESSIONS'],
  LEAD_GENERATION: ['IMPRESSIONS'],
  OFFSITE_CONVERSIONS: ['IMPRESSIONS'],
  APP_INSTALLS: ['IMPRESSIONS'],
  LINK_CLICKS: ['LINK_CLICKS', 'IMPRESSIONS'],
};

// Helper function to get human-readable label for an optimization goal
export const getOptimizationGoalLabel = (goal: string): string => {
  return (
    OPTIMIZATION_GOAL_LABELS[goal] ||
    goal
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase())
  );
};

// Helper function to get valid optimization goals for a campaign objective
export const getValidOptimizationGoals = (campaignObjective: string | undefined): string[] => {
  return campaignObjective ? VALID_OPTIMIZATION_GOALS[campaignObjective] || [] : [];
};

// Helper function to get valid billing events for an optimization goal
export const getValidBillingEvents = (optimizationGoal: string | undefined): string[] => {
  return optimizationGoal ? VALID_BILLING_EVENTS[optimizationGoal] || [] : [];
};
